import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { DialogMessage, DialogMessageAttachmentModel, DialogMessageMarkMessagesAsReadModel } from '../../model/dialog';
import { ChatService } from '../../service/chat-service';
import { getTranslation } from '../../../core/helper/translation';
import { ChatMessageComponent } from './chat-message/chat-message.component';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { concatMap, filter, from, Subscription, tap } from 'rxjs';
import { LanguageService } from '../../../core/service/language.service';

export interface ChatInfo {
  date: string;
  stringRepresentation: string;
  chatMessages: DialogMessage[];
}

export interface ChatEmitInfo {
  messageCount: number;
  attachments: DialogMessageAttachmentModel[];
}

@Component({
    selector: 'app-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatComponent implements OnInit, OnDestroy {
  constructor(
    private readonly _chatService: ChatService,
    private readonly _languageService: LanguageService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _fc: FormControlExFactory
  ) {}

  chatInfo: ChatInfo[];
  checkOnlyForTenantControl: FormControlEx<boolean>;
  files: DialogMessageAttachmentModel[] = [];
  dialogMessages: DialogMessage[] = [];
  hasIntersected: boolean = false;
  messageCheckAsReadBlocked: boolean;
  private _attachmentsSub: Subscription;

  @Input() dialogId: string;
  @Input({ required: true }) dialogEndpoint!: string;
  @Input() hasMessageInput: boolean = true;
  @Input() visibleForTenantCheckbox: boolean = true;
  @Input() isTenantInvolved: boolean = true;
  @Input() onlyDialogTab: boolean = true;
  @ViewChild('chatContent') chatContent: ElementRef;
  @ViewChildren(ChatMessageComponent) childrenComponents!: QueryList<ChatMessageComponent>;
  @Output() dialogChange: EventEmitter<ChatEmitInfo> = new EventEmitter<ChatEmitInfo>();

  ngOnInit(): void {
    this.checkOnlyForTenantControl = this._fc.create('CHAT.MESSAGE.SHOW_WITH_TENANT', false);

    if (!this.isTenantInvolved) {
      this.checkOnlyForTenantControl.setValue(false);
      this.checkOnlyForTenantControl.disable({ emitEvent: false });
    }

    this._chatService.getDialogMessages(this.dialogId, this.dialogEndpoint).subscribe(x => {
      this.dialogMessages = x.dialogMessages;
      this.prepareChatInfoBlocks(true);
      this._processAttachments();
      this.createChatObserver();
    });
  }

  ngOnDestroy(): void {
    this._attachmentsSub?.unsubscribe();
  }

  private _processAttachments() {
    this.files = [];
    this.dialogChange.emit({ messageCount: this.dialogMessages?.length ?? 0, attachments: this.files });
    this._downloadAttachments();
  }

  private _downloadAttachments() {
    this._attachmentsSub?.unsubscribe();
    this._attachmentsSub = from([...this.dialogMessages].reverse())
      .pipe(
        filter(dialogMessage => dialogMessage.attachments?.length > 0),
        concatMap(dialogMessage =>
          this._chatService.getDialogMessageAttachments(dialogMessage.id, this.dialogEndpoint).pipe(
            tap(attachments => {
              // Apply attachments to files tab
              setTimeout(() => {
                this.files.unshift(...attachments);
                this._cdr.detectChanges();
              }, 100);

              dialogMessage.attachments = attachments;
              this.childrenComponents.find(x => x.dialogMessage.id === dialogMessage.id)?.rerenderAttachments();
            })
          )
        )
      )
      .subscribe();
  }

  private createChatObserver() {
    const observer = new IntersectionObserver(
      entries => {
        if (!this.hasIntersected) {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.hasIntersected = true;
              this.onIntersect();
              observer.disconnect();
            }
          });
        }
      },
      { threshold: 1 }
    );

    observer.observe(this.chatContent?.nativeElement);
  }

  private onIntersect() {
    const canReadMessages = this.dialogMessages.filter(x => x.canMarkAsRead);
    if (canReadMessages.length > 0) {
      const model = new DialogMessageMarkMessagesAsReadModel();
      model.ids = canReadMessages.map(x => x.id);
      this.messageCheckAsReadBlocked = true;

      this._chatService.markMessagesAsRead(model, this.dialogEndpoint).subscribe(markedMessages => {
        markedMessages.forEach(message => {
          const currentMessage = this.dialogMessages.find(x => x.id === message.id);
          if (currentMessage) {
            currentMessage.isRead = message.isRead;
            currentMessage.canMarkAsUnread = message.canMarkAsUnread;
            currentMessage.canMarkAsRead = message.canMarkAsRead;
            currentMessage.readTime = message.readTime;
            currentMessage.readByUser = message.readByUser;
          }

          this.messageCheckAsReadBlocked = false;
        });
      });
    }
  }

  onTenantFilterChange() {
    this.prepareChatInfoBlocks(false);
  }

  prepareChatInfoBlocks(moveToLastItem: boolean) {
    this.chatInfo = [];
    const isTenantOnlyFilterEnabled = this.isTenantInvolved && !!this.checkOnlyForTenantControl.value;
    const messagesToProcess = isTenantOnlyFilterEnabled ? this.dialogMessages.filter(x => x.showToTenant) : this.dialogMessages;

    messagesToProcess
      .sort((a, b) => new Date(a.sendTime).getTime() - new Date(b.sendTime).getTime())
      .forEach(item => {
        const date = this._formatChatDate(item.sendTime);
        const infoBlock = this.chatInfo.find(x => x.date === date);
        if (!infoBlock) {
          const datePrefix = this._getStringRepresentationPrefix(item.sendTime.toDateString());
          const chatInfoItem = {
            date: date,
            stringRepresentation: datePrefix ? `${datePrefix} ${date}` : date,
            chatMessages: [],
          };
          chatInfoItem.chatMessages.push(item);
          this.chatInfo.push(chatInfoItem);
        } else {
          infoBlock.chatMessages.push(item);
        }
      });

    if (this.chatContent && moveToLastItem) {
      setTimeout(() => {
        this.chatContent.nativeElement.scrollTop = this.chatContent.nativeElement.scrollHeight;
      }, 100);
    }
  }

  private _getStringRepresentationPrefix(date: string) {
    const translator = this._languageService.getCurrentTranslations();
    const today = new Date();
    if (today.toDateString() === date) {
      return getTranslation(translator, 'CHAT.MESSAGE.TODAY') + ', ';
    }

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (yesterday.toDateString() === date) {
      return getTranslation(translator, 'CHAT.MESSAGE.YESTERDAY') + ', ';
    }

    return null;
  }

  handleMessageSent() {
    this._chatService.getDialogMessages(this.dialogId, this.dialogEndpoint).subscribe(x => {
      this.dialogMessages = x.dialogMessages;
      this.prepareChatInfoBlocks(true);
      this._processAttachments();
    });
  }

  handleMessageDelete(messageId: string) {
    const messageIndex = this.dialogMessages.findIndex(x => x.id === messageId);

    if (messageIndex !== -1) {
      const attachmentsCount = this.dialogMessages[messageIndex].attachments.length;
      this.dialogMessages = this.dialogMessages.filter(x => x.id !== messageId);

      const attachmentsToRemove = this.dialogMessages.slice(0, messageIndex).reduce((sum, msg) => sum + msg.attachments.length, 0);

      this.files.splice(attachmentsToRemove, attachmentsCount);
      this.prepareChatInfoBlocks(false);
    }
  }

  private _formatChatDate(date: Date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
